import React, { useState, useEffect, useMemo } from 'react';
import { debounceSetCookieKeepVisitorSession, isMobile, upsertTempiCookie, getTempiSmartPopupIds, setTempiSmartPopupIds } from './utils.es.js';
import { UNIDENTIFIED, NEW_USER } from './constants.es.js';
import { buildComponentDict, BuilderComponent } from '@tempi/core-renderer';
import { components } from '@tempi/core-elements/dist/renderer';
import styled from '@emotion/styled';
import { useLocationChange } from './hooks/useLocationChange.es.js';
import { OnSiteDiscoveryService } from './handlers/api.es.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const makeBuilderConfig = (origin) => {
  if (origin["ROOT"])
    delete origin["ROOT"];
  const childrenOfRoot = Object.keys(origin).filter(
    (key) => origin[key].parentId === "ROOT"
  );
  return __spreadProps(__spreadValues({}, origin), {
    ROOT: {
      id: "ROOT",
      parentId: null,
      tag: "popuproot",
      resolvedName: "PopupRootResolver",
      style: {},
      isActive: false,
      isCanvas: true,
      customAttributes: {
        root: {}
      },
      children: childrenOfRoot
    }
  });
};
const isInIframe = () => typeof window !== void 0 && window.self !== window.top;
const SmartComponent = ({
  configs,
  env
}) => {
  const [builderConfig, setBuilderConfig] = useState();
  const componentDict = buildComponentDict(components);
  const _a = configs || {}, { iamId, appId, trackingAppId, sessionPeriodMinutes } = _a, rest = __objRest(_a, ["iamId", "appId", "trackingAppId", "sessionPeriodMinutes"]);
  const handleSubscribe = async () => {
    if (isInIframe())
      return;
    if (!appId)
      return;
    setBuilderConfig(void 0);
    const { tempiVp, tempiVs } = upsertTempiCookie(iamId, sessionPeriodMinutes);
    const isNewVisitor = tempiVp === UNIDENTIFIED && tempiVs === NEW_USER;
    try {
      const discoveryService = new OnSiteDiscoveryService(env);
      const body = {
        appId,
        iamId,
        trackingAppId,
        onlyNewVisitor: isNewVisitor,
        url: window.location.href
      };
      const tempiSmartPopupIds = getTempiSmartPopupIds();
      const popups = await discoveryService.subscribeSmartPopup(body);
      const activePopups = popups.filter(
        (item) => !tempiSmartPopupIds.includes(item.id)
      );
      if (!(activePopups == null ? void 0 : activePopups.length))
        return;
      tempiSmartPopupIds.push(...activePopups.map((el) => el.id));
      setTempiSmartPopupIds(tempiSmartPopupIds);
      setBuilderConfig(
        activePopups.reduce((acc, curr) => __spreadValues(__spreadValues({}, acc), curr.content), {})
      );
    } catch (e) {
      console.error(e);
    }
  };
  useEffect(() => {
    if (!appId)
      return;
    handleSubscribe();
  }, [appId]);
  useLocationChange(handleSubscribe);
  useEffect(() => {
    ["click", "scroll", "keypress"].forEach((evt) => {
      var _a2;
      (_a2 = window == null ? void 0 : window.document) == null ? void 0 : _a2.addEventListener(
        evt,
        () => debounceSetCookieKeepVisitorSession(sessionPeriodMinutes)
      );
    });
    return () => {
      ["click", "scroll", "keypress"].forEach((evt) => {
        var _a2;
        (_a2 = window == null ? void 0 : window.document) == null ? void 0 : _a2.removeEventListener(
          evt,
          () => debounceSetCookieKeepVisitorSession(sessionPeriodMinutes)
        );
      });
    };
  }, [configs == null ? void 0 : configs.sessionPeriodMinutes]);
  const content = useMemo(() => {
    if (!builderConfig)
      return;
    return makeBuilderConfig(builderConfig);
  }, [builderConfig]);
  if (!appId || !content || isInIframe())
    return null;
  const device = isMobile() ? "mobile" : "desktop";
  return /* @__PURE__ */ React.createElement(Wrapper, null, /* @__PURE__ */ React.createElement(BuilderComponent, {
    content,
    configs: __spreadValues({ env, device }, rest),
    componentDict
  }));
};
const Wrapper = styled.div`
  /* Reset style */
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji',
    'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  color: #212529;
  background-color: #fff;
  -webkit-text-size-adjust: 100%;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
`;

export { SmartComponent as default };
